import { Button, Stack, SxProps, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { makeStyles } from 'tss-react/mui'
import { isNil } from 'lodash'

interface StyleProps {
  isLeftControlsActive: boolean
}
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  leftControls: {
    margin: theme.spacing(2, 0),
    [theme.breakpoints.up('md')]: {
      margin: 0,
      marginRight: 'auto',
    },
  },
  rightButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      marginLeft: 'auto',
    },
    gap: theme.spacing(2),
  },
  stackContainer: {
    display: 'flex',
    flexDirection: props.isLeftControlsActive ? 'row' : 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: props.isLeftControlsActive ? 'row' : 'row-reverse',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
}))

export interface FormProps<T> {
  model: T
  isLoading?: boolean
  leftControls?: React.ReactNode
  hasDivider?: boolean
  rightControlsStyles?: SxProps
  stackContainerStyles?: string
  containerStyle?: SxProps
  children?: React.ReactNode
}

const SettingsFormContainer: React.FC<FormProps<unknown>> = ({ children, model, isLoading, leftControls, stackContainerStyles, containerStyle }) => {
  const isLeftControlsActive = !isNil(leftControls)
  const { classes, cx } = useStyles({ isLeftControlsActive })
  const { t } = useCustomTranslation()

  const { resetForm, dirty, isValid, handleSubmit } = useFormikContext<unknown>()
  const { breakpoints } = useTheme()
  const submitIsDisabled = useMemo(() => {
    return !dirty || !isValid
  }, [dirty, isValid])

  const handleResetForm = useCallback(() => {
    resetForm()
  }, [resetForm])

  const fullWidthButtons = useMediaQuery(breakpoints.down('md'))

  return (
    <form onSubmit={handleSubmit}>
      <Stack sx={{ p: 4, pb: 2, pt: 2, ...containerStyle }}>{children}</Stack>
      <Stack className={cx(classes.stackContainer, stackContainerStyles)}>
        {leftControls && <Stack className={classes.leftControls}>{leftControls}</Stack>}
        <Stack className={classes.rightButtons}>
          <Button
            type="button"
            variant="outlined"
            size="large"
            color="inherit"
            disabled={!dirty}
            onClick={handleResetForm}
            fullWidth={fullWidthButtons}
            sx={{ width: '100%', minWidth: 'auto' }}
          >
            {t('common.reset')}
          </Button>
          <ButtonWithLoading
            fullWidth={fullWidthButtons}
            size="large"
            color="primary"
            disabled={submitIsDisabled}
            isLoading={isLoading}
            onClick={handleSubmit}
            label={t('common.save')}
            buttonProps={{ type: 'submit' }}
          />
        </Stack>
      </Stack>
    </form>
  )
}

export default SettingsFormContainer
